import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { TMyMeeting } from '@/common/basis/types/database/tmy-meeting';
import { TField } from '@/common/basis/types/tfield';
import { useMeetingType } from '@/common/hooks/meeting-type';
import { openJoinroomUrl } from '@/common/utils/window/webview';
import CheckBeforeJoin from '@/components/HOC/CheckBeforeJoin';

function App(props: {
  index: number;
  list: TMyMeeting.ProgressItem[];
  setIsOpenProgressDetail: (isOpenProgressDetail: boolean) => void;
  setCurrentProgressIndex: (currentProgressIndex: number) => void;
}) {
  const index = props.index;
  const list = props.list;
  const setIsOpenProgressDetail = props.setIsOpenProgressDetail;
  const setCurrentProgressIndex = props.setCurrentProgressIndex;
  const item = useMemo(() => list[index], [index, list]);
  const startedTime = useMemo(() => item?.startedTime, [item?.startedTime]);
  const myStatus = useMemo(() => item?.myStatus, [item?.myStatus]);
  const isUserJoinMeet = useMemo(() => myStatus === TField.USER_MEET_STATUS_2, [myStatus]);
  const roomName = useMemo(() => item?.roomName ?? '', [item?.roomName]);
  const roomId = useMemo(() => item?.roomId ?? '', [item?.roomId]);
  const meetingType = useMemo(() => item?.meetingType ?? TField.MEETING_TYPE_QUICK, [item?.meetingType]);
  const { isMeetingLive } = useMeetingType(meetingType);
  const [currentDuration, setCurrentDuration] = useState(0);
  const currentDurationStr = useMemo(() => {
    const oneHour = currentDuration > 60 * 60 * 1000; // 大于1小时: HH:mm:ss
    const formatStr = oneHour ? 'HH:mm:ss' : 'mm:ss';
    const hour = parseInt(`${dayjs.duration(currentDuration).asHours()}`);

    return hour >= 24
      ? `${hour}:${dayjs.duration(currentDuration).format('mm:ss')}`
      : dayjs.duration(currentDuration).format(formatStr);
  }, [currentDuration]);
  const repeatType = useMemo(() => item?.repeatType, [item?.repeatType]);
  const { t } = useTranslation();
  const isRepeatMeeting = useMemo(() => repeatType && repeatType !== TField.MEET_REPEAT_TYPE_NONE, [repeatType]);
  const repeatStr = useMemo(() => (isRepeatMeeting ? t('cycle') : ''), [isRepeatMeeting]);

  useEffect(() => {
    if (startedTime) {
      setCurrentDuration(Date.now() - startedTime); // 先执行一次
      setInterval(() => {
        setCurrentDuration(Date.now() - startedTime);
      }, 1000);
    }
  }, [startedTime]);

  return (
    <>
      <div className='mb-3 cursor-pointer'>
        <div
          className={`flex h-14 items-center justify-between rounded-md ${isMeetingLive ? 'bg-[rgba(225,124,218,0.09)]' : 'bg-[rgba(79,198,126,0.09)]'} py-[7px]`}
        >
          <div
            className='flex flex-1 items-center justify-start'
            onClick={() => {
              setCurrentProgressIndex(index);
              setIsOpenProgressDetail(true);
            }}
          >
            <div className='h-8 overflow-hidden px-3'>
              {isMeetingLive ? (
                <img className='h-8 w-8' src={require('@/common/basis/assets/live_meeting_icon.png')} />
              ) : (
                <img className='h-8 w-8' src={require('@/common/basis/assets/video_meeting_icon.png')} />
              )}
            </div>
            <div className='flex flex-1 items-center justify-between'>
              <div>
                <div className='flex h-5 items-center justify-start'>
                  <div className='line-clamp-1 overflow-hidden text-[15px]' style={{ wordBreak: 'break-word' }}>
                    <span>{roomName}</span>
                  </div>
                  {repeatStr ? (
                    <Tag className='ml-2 flex h-[18px] items-center justify-center px-1 text-[12px]' color='green'>
                      {repeatStr}
                    </Tag>
                  ) : (
                    <></>
                  )}
                </div>
                <div
                  className={`mt-1 flex items-center transition-all ${
                    currentDuration > 0 ? 'opacity-100' : 'opacity-0'
                  } justify-start text-[12px] text-[#aaa]`}
                >
                  <span>{currentDurationStr}</span>
                  <span className='mx-1 h-[10px] w-[1px] overflow-hidden bg-[rgba(19,20,21,.22)]' />
                  <span>{roomId}</span>
                </div>
              </div>
            </div>
          </div>

          <div className='relative mr-3'>
            <CheckBeforeJoin callback={(success) => success && openJoinroomUrl({ roomId })} roomId={roomId}>
              {isUserJoinMeet ? (
                <Button
                  className={`${isMeetingLive ? 'border-[#E17CDA]' : 'border-[#4fc67e]'} px-3 text-[12px]`}
                  ghost
                  type='primary'
                >
                  <span className={`${isMeetingLive ? 'text-[#E17CDA]' : 'text-[#4fc67e]'}`}>{t('joined')}</span>
                </Button>
              ) : (
                <Button
                  className={`${isMeetingLive ? 'bg-[#E17CDA]' : 'bg-[#4fc67e]'} px-3 text-[12px]`}
                  type='primary'
                >
                  <span>{t('join')}</span>
                </Button>
              )}
            </CheckBeforeJoin>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
